// @flow strict

import * as React from 'react';
import Slider from 'react-slick';
import cs from 'classnames';

import RichText from '../RichText';
import { Link } from '../../internal/Button';
import { Columns } from '../../internal/Columns';
import PageContentBlock from '../../internal/PageContentBlock';
import ResponsiveImage from '../../internal/ResponsiveImage';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './styles.scss';

type Theme = 'Dark' | 'Light' | 'Citrus' | 'Neutral' | 'DarkNeutral';

type CardType = {|
  image: string,
  alt: string,
  href: string,
  title?: string,
  description: string,
  theme?: Theme,
|};

type PropTypes = {|
  title?: string,
  intro?: string,
  cards: Array<CardType>,
  theme?: Theme,
  isCarousel?: boolean,
|};

const blockVariantMap = {
  Dark: 'Dark',
  Light: 'Default',
  Citrus: 'Featured',
  Neutral: 'DefaultContrast',
  DarkNeutral: 'DefaultContrast',
};

const linkVariantMap = {
  Dark: 'UnderlineDark',
  Light: 'Underline',
  Citrus: 'UnderlineHigh',
  Neutral: 'Underline',
  DarkNeutral: 'Underline',
};

const reactSlickSettings = {
  infinite: true,
  speed: 500,
  autoplaySpeed: 4500,
  slidesToShow: 3,
  slidesToScroll: 1,
  autoplay: true,
  centerMode: true,
  arrows: true,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
        centerMode: false,
      },
    },
    {
      breakpoint: 700,
      settings: {
        slidesToShow: 1,
        centerMode: true,
      },
    },
  ],
};

const Card = ({ theme = 'Citrus', ...props }: CardType) => (
  <div>
    <div className={styles.card}>
      <ResponsiveImage
        className={styles.image}
        src={props.image}
        alt={props.alt}
        defaultSize={{ width: 320, height: 200 }}
      />
      <div className={styles.details}>
        {props.title && (
          <Link className={styles.cardTitle} href={props.href} variant={linkVariantMap[theme]}>
            {props.title}
          </Link>
        )}

        <div className={cs(styles.description, styles[`description--${theme}`])}>
          <RichText background="NoMargin">{props.description}</RichText>
        </div>
      </div>
    </div>
  </div>
);

export default ({ theme = 'Citrus', ...props }: PropTypes) => {
  if (props.cards.length === 0) {
    return null;
  }
  if (props.cards.length <= 3) {
    return (
      <PageContentBlock background={blockVariantMap[theme]} title={props.title} intro={props.intro}>
        <div className={styles.fallbackContainer}>
          {props.cards.map((card, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Card {...card} key={index} theme={theme} />
          ))}
        </div>
      </PageContentBlock>
    );
  }

  if (typeof window === 'undefined') {
    return (
      <PageContentBlock background={blockVariantMap[theme]} title={props.title} intro={props.intro}>
        {''}
      </PageContentBlock>
    );
  }

  return (
    <PageContentBlock background={blockVariantMap[theme]} title={props.title} intro={props.intro}>
      {props.isCarousel ? (
        <Slider {...reactSlickSettings}>
          {props.cards.map((card, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Card {...card} key={index} theme={theme} />
          ))}
        </Slider>
      ) : (
        <Columns columns={4} centerColumns fitBestColumns>
          {props.cards.map((card, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Card {...card} key={index} theme={theme} />
          ))}
        </Columns>
      )}
    </PageContentBlock>
  );
};
